import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from './ProtectedRoute';
import { ComingSoonPage } from '@/pages/ComingSoonPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { LoadingState } from '@/components/common/states';

// Login is eager (pre-auth entry). Every protected screen is code-split so the
// initial bundle stays lean and heavy deps (charts) load only with their route.
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const AnalyticsPage = lazy(() => import('@/features/analytics/AnalyticsPage'));
const UsersPage = lazy(() => import('@/features/users/UsersPage'));
const UserDetailPage = lazy(() => import('@/features/users/UserDetailPage'));
const RechargesPage = lazy(() => import('@/features/finance/RechargesPage'));
const WithdrawalsPage = lazy(() => import('@/features/finance/WithdrawalsPage'));
const PaymentMethodsPage = lazy(() => import('@/features/finance/PaymentMethodsPage'));
const LedgerPage = lazy(() => import('@/features/finance/LedgerPage'));
const BetsPage = lazy(() => import('@/features/games/BetsPage'));
const IssuesPage = lazy(() => import('@/features/games/IssuesPage'));
const GamesConfigPage = lazy(() => import('@/features/games/GamesConfigPage'));
const PnlPage = lazy(() => import('@/features/games/PnlPage'));
const FundistProvidersPage = lazy(() => import('@/features/fundist/ProvidersPage'));
const VipPage = lazy(() => import('@/features/engagement/VipPage'));
const ActivitiesPage = lazy(() => import('@/features/engagement/ActivitiesPage'));
const PointMallPage = lazy(() => import('@/features/engagement/PointMallPage'));
const AffiliatesPage = lazy(() => import('@/features/engagement/AffiliatesPage'));
const BannersPage = lazy(() => import('@/features/content/BannersPage'));
const NoticesPage = lazy(() => import('@/features/content/NoticesPage'));
const CustomerServicePage = lazy(() => import('@/features/content/CustomerServicePage'));
const SettingsPage = lazy(() => import('@/features/content/SettingsPage'));
const AuditPage = lazy(() => import('@/features/audit/AuditPage'));
const AdminUsersPage = lazy(() => import('@/features/admins/AdminUsersPage'));
const KycPage = lazy(() => import('@/features/compliance/KycPage'));
const RgPage = lazy(() => import('@/features/compliance/RgPage'));
const RiskPage = lazy(() => import('@/features/compliance/RiskPage'));
const ReconciliationPage = lazy(() => import('@/features/compliance/ReconciliationPage'));

// Serve-path aware: when the app is built with a Vite `base` (e.g. /cms/ so the
// CMS can be hosted at api.hgnice.app/cms), react-router must use the same
// basename. Defaults to '/' in dev.
const ROUTER_BASENAME =
  (import.meta.env.BASE_URL || '/').replace(/\/$/, '') || '/';

export function App() {
  return (
    <BrowserRouter basename={ROUTER_BASENAME}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route
              element={
                <Suspense fallback={<LoadingState />}>
                  <Outlet />
                </Suspense>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />

              <Route path="users" element={<UsersPage />} />
              <Route path="users/:id" element={<UserDetailPage />} />

              <Route path="finance/recharges" element={<RechargesPage />} />
              <Route path="finance/withdrawals" element={<WithdrawalsPage />} />
              <Route path="finance/methods" element={<PaymentMethodsPage />} />
              <Route path="finance/ledger" element={<LedgerPage />} />
              <Route path="finance/reconciliation" element={<ReconciliationPage />} />

              <Route path="games/bets" element={<BetsPage />} />
              <Route path="games/issues" element={<IssuesPage />} />
              <Route path="games/control-room" element={<ComingSoonPage title="Live Control Room" />} />
              <Route path="games/odds" element={<GamesConfigPage />} />
              <Route path="games/pnl" element={<PnlPage />} />
              <Route path="games/providers" element={<FundistProvidersPage />} />

              <Route path="engagement/activities" element={<ActivitiesPage />} />
              <Route path="engagement/vip" element={<VipPage />} />
              <Route path="engagement/point-mall" element={<PointMallPage />} />
              <Route path="engagement/affiliates" element={<AffiliatesPage />} />

              <Route path="content/banners" element={<BannersPage />} />
              <Route path="content/notices" element={<NoticesPage />} />
              <Route path="content/customer-service" element={<CustomerServicePage />} />
              <Route path="content/settings" element={<SettingsPage />} />

              <Route path="audit" element={<AuditPage />} />
              <Route path="compliance/kyc" element={<KycPage />} />
              <Route path="compliance/responsible-gambling" element={<RgPage />} />
              <Route path="risk" element={<RiskPage />} />
              <Route path="admins" element={<AdminUsersPage />} />
              <Route path="platform/flags" element={<ComingSoonPage title="Feature Flags" />} />
              <Route path="platform/i18n" element={<ComingSoonPage title="Localization" />} />

              <Route path="404" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
